import {h, Fragment} from "preact"
import { useFacet } from "@nosto/preact"
import { useEffect } from "preact/hooks"
import Checkbox from "./elements/Checkbox"

// Handleize function to normalize strings
const handleize = value => {
    return value
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-') 
        .replace(/-+/g, '-') 
        .replace(/^-|-$/g, '') 
}

// Custom ToggleSwitch component for iPhone-like toggle
function ToggleSwitch({ value, selected, onChange }) {
    return (
        <label className="toggle-switch">
            <input
                type="checkbox"
                checked={selected}
                onChange={onChange}
                aria-label={`Toggle ${value}`}
            />
            <span className="toggle-slider"></span>
        </label>
    )
}

export default function Facet({ facet }) {
    const { active, selectedFiltersCount, toggleActive, toggleProductFilter } = useFacet(facet)
    const isGridFacet = facet.name.toLowerCase() === 'size' || facet.name.toLowerCase() === 'gauge'
    const isStoneFacet = facet.name.toLowerCase().includes('stone') 
    const isToggleFacet = facet.name.toLowerCase().includes('new') || facet.name.toLowerCase().includes('sale')

    useEffect(() => {
        const isDesktop = window.matchMedia("(min-width: 768px)").matches
        if (isDesktop && !active && !isToggleFacet) {
            toggleActive()
        }
    }, []) 

    // Function to handle filter toggle and scroll to top
    const handleFilterToggle = (field, value, selected) => {
        toggleProductFilter(field, value, selected)
        window.scrollTo({ top: 0, behavior: 'smooth' })
    }

    return (
        <li className={`ns-sidebar-dropdown ${isToggleFacet ? '' : active ? 'ns-active border-b border-solid color-border-dark' : 'border-b border-solid color-border-dark'}`} aria-expanded={isToggleFacet ? true : !!active}>
            {isToggleFacet ? (
                <div className="ns-sidebar-submenu ">
                    <div className="ns-d-flex ns-justify-content-between ns-align-items-center ns-font-3">
                        <span className="toggle-facet-title text-[18px]">{facet.name}</span>
                        {facet.data
                            ?.filter(value => value.value === 'true') 
                            .map(value => (
                                <ToggleSwitch
                                    key={value.value}
                                    value={value.value}
                                    selected={value.selected}
                                    onChange={e => {
                                        e.preventDefault()
                                        handleFilterToggle(facet.field, value.value, !value.selected)
                                    }}
                                />
                            ))}
                    </div>
                </div>
     
            ) : (
                <>
                    <span
                        className="ns-anchor ns-d-inline-block ns-relative ns-w-100 ns-border-box"
                        data-nosto-element="facet"
                        onClick={toggleActive}
                        aria-controls={`${facet.id}-sub-menu`}
                        aria-label={`${active ? 'Collapse' : 'Expand'} ${facet.name}`}
                    >
                        <span className="color-text text-[18px]">{facet.name}</span>
                        {active ? (
                            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="25" viewBox="0 0 24 25" fill="none" style={{ verticalAlign: 'middle', marginLeft: '8px' }}>
                                <path d="M18 12.5H6" stroke="#fff" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                            </svg>
                        ) : (
                            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="25" viewBox="0 0 24 25" fill="none" style={{ verticalAlign: 'middle', marginLeft: '8px' }}>
                                <path d="M12 6.21875V12.2188M12 12.2188V18.2188M12 12.2188H18M12 12.2188L6 12.2187" stroke="#fff" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                            </svg>
                        )}
                    </span>
                    <div className={`ns-sidebar-submenu ${active ? '' : 'ns-d-none'}`} id={`${facet.id}-sub-menu`}>
                        {isGridFacet ? (
                            <ul className="grid-facet">
                                {facet.data
                                    ?.slice()
                                    .sort((a, b) => {
                                        const numA = Number(a.value)
                                        const numB = Number(b.value)
                                        const isNumA = !isNaN(numA)
                                        const isNumB = !isNaN(numB)
                                        if (isNumA && isNumB) return numA - numB
                                        return a.value.localeCompare(b.value, undefined, { numeric: true, sensitivity: 'base' })
                                    })
                                    .map(value => (
                                        <li
                                            key={value.value}
                                            className={`size-facet ${value.selected ? 'border border-solid color-border-dark' : 'not-selected'}`}
                                            data-nosto-element="facet-setting"
                                        >
                                            <Checkbox
                                                value={value.value}
                                                selected={value.selected}
                                                onChange={e => {
                                                    e.preventDefault()
                                                    handleFilterToggle(facet.field, value.value, !value.selected)
                                                }}
                                            />
                                        </li>
                                    ))}
                            </ul>
                        ) : isStoneFacet ? (
                            <ul className="ns-list-unstyled ns-p-0 ns-m-0 stone-facet">
                                {facet.data?.map(value => (
                                    <li
                                        key={value.value}
                                        className="ns-d-flex ns-justify-content-between ns-align-items-center ns-font-3"
                                        data-nosto-element="facet-setting"
                                    >
                                        <Checkbox
                                            value={value.value}
                                            count={value.count}
                                            selected={value.selected}
                                            onChange={e => {
                                                e.preventDefault()
                                                handleFilterToggle(facet.field, value.value, !value.selected)
                                            }}
                                        />
                                    </li>
                                ))}
                            </ul>
                        )  : (
                            <ul className="ns-list-unstyled ns-p-0 ns-m-0">
                                {facet.data?.map(value => (
                                    <li
                                        key={value.value}
                                        className="ns-d-flex ns-justify-content-between ns-align-items-center ns-font-3"
                                        data-nosto-element="facet-setting"
                                    >
                                        <Checkbox
                                            value={value.value}
                                            count={value.count}
                                            selected={value.selected}
                                            onChange={e => {
                                                e.preventDefault()
                                                handleFilterToggle(facet.field, value.value, !value.selected)
                                            }}
                                        />
                                    </li>
                                ))}
                            </ul>
                        )}
                    </div>
                </>
            )}
        </li>
    )
}